import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import { styled } from '@mui/material/styles';
import { useState } from 'react';

/* ── Styles ─────────────────────────────────────────────────────── */

const BarWrapper = styled('div')({
    position: 'relative',
    height: 32,
    background: '#0d0d17',
    borderBottom: '1px solid #2a2a3a',
    overflow: 'hidden',
    userSelect: 'none',
});

const LogoBlock = styled('div')({
    position: 'absolute',
    top: 4,
    height: 24,
    background: 'rgba(206, 147, 216, 0.25)',
    border: '1px solid rgba(206, 147, 216, 0.6)',
    borderRadius: 3,
    display: 'flex',
    alignItems: 'center',
    paddingLeft: 6,
    paddingRight: 6,
    gap: 4,
    overflow: 'hidden',
    minWidth: 10,
    boxSizing: 'border-box',
});

const HandleEdge = styled('div')({
    position: 'absolute',
    top: 4,
    width: 6,
    height: 24,
    cursor: 'ew-resize',
    zIndex: 2,
    '&:hover': { background: 'rgba(206,147,216,0.4)', borderRadius: 2 },
});

const BlockLabel = styled('span')({
    fontSize: 12,
    color: '#ce93d8',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    userSelect: 'none',
    pointerEvents: 'none',
    flex: 1,
});

/**
 * LogoTrackBar — renders a purple block spanning [track.startTime, track.endTime].
 * Fully prop-driven; left/right edge drag adjusts times; center drag moves both.
 */
export default function LogoTrackBar({ track, duration, zoomLevel, onUpdate }) {
    const [dragging, setDragging] = useState(null); // 'left' | 'right' | 'body' | null

    if (!track) return <BarWrapper />;

    const blockLeft = track.startTime * zoomLevel;
    const blockWidth = Math.max((track.endTime - track.startTime) * zoomLevel, 8);

    /* ── Generic drag helper ─────────────────────────────────── */
    const startDrag = (kind, e) => {
        e.preventDefault();
        const startX = e.clientX;
        const { startTime, endTime } = track;

        const onMove = (me) => {
            const dx = me.clientX - startX;
            const dt = dx / zoomLevel;
            if (kind === 'left') {
                const newStart = Math.max(0, Math.min(startTime + dt, endTime - 0.5));
                onUpdate({ startTime: parseFloat(newStart.toFixed(2)) });
            } else if (kind === 'right') {
                const newEnd = Math.max(startTime + 0.5, Math.min(endTime + dt, duration));
                onUpdate({ endTime: parseFloat(newEnd.toFixed(2)) });
            } else {
                // body — move both
                const span = endTime - startTime;
                const newStart = Math.max(0, Math.min(startTime + dt, duration - span));
                onUpdate({
                    startTime: parseFloat(newStart.toFixed(2)),
                    endTime: parseFloat((newStart + span).toFixed(2)),
                });
            }
        };
        const onUp = () => {
            setDragging(null);
            window.removeEventListener('mousemove', onMove);
            window.removeEventListener('mouseup', onUp);
        };
        setDragging(kind);
        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
    };

    return (
        <BarWrapper>
            {/* Left resize handle */}
            <HandleEdge
                style={{ left: blockLeft }}
                onMouseDown={(e) => startDrag('left', e)}
            />

            {/* Main block */}
            <LogoBlock
                style={{ left: blockLeft + 5, width: Math.max(blockWidth - 10, 4), cursor: dragging === 'body' ? 'grabbing' : 'grab' }}
                onMouseDown={(e) => startDrag('body', e)}
                title={`${track.name}\n${track.position}  •  opacity: ${Math.round(track.opacity * 100)}%\n${track.startTime.toFixed(1)}s → ${track.endTime.toFixed(1)}s`}
            >
                <BrandingWatermarkIcon
                    style={{ fontSize: 12, color: '#ce93d8', flexShrink: 0, pointerEvents: 'none' }}
                />
                {blockWidth > 50 && <BlockLabel>{track.labelRole || track.name}</BlockLabel>}
            </LogoBlock>

            {/* Right resize handle */}
            <HandleEdge
                style={{ left: blockLeft + blockWidth - 6 }}
                onMouseDown={(e) => startDrag('right', e)}
            />
        </BarWrapper>
    );
}
